import React, { useEffect, useState } from 'react';

import {
  ChevronDown,
  ChevronUp,
  CircleUserRound,
  Landmark,
  X,
} from 'lucide-react';
import { Label, TextInput } from 'flowbite-react';
import { gamesApi } from '../../api/games';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';

export default function AddGame({
  isOpen,
  onClose,
  allGameSettings,
  refreshGames,
}) {
  const { tokens, user } = useAuth();
  const [userGamesDropdownOpen, setUserGamesDropdownOpen] = useState(false);
  const [gameSettingsDropdownOpen, setGameSettingsDropdownOpen] =
    useState(false);
  const [selectedGame, setSelectedGame] = useState('');
  const [visible, setVisible] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [userGames, setUserGames] = useState([]);

  const [name, setName] = useState('');
  const [commissionRate, setCommissionRate] = useState('');
  const [winRate, setWinRate] = useState('');
  const [doubleWinRate, setDoubleWinRate] = useState('');
  const [tripleWinRate, setTripleWinRate] = useState('');
  const [gameSettingsId, setGameSettingsId] = useState('');
  const [gameId, setGameId] = useState('');
  const [user_id, setUserId] = useState(user.id);
  const [gameSettingsOptions, setGameSettingsOptions] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setVisible(true);
      setTimeout(() => setMounted(true), 10);
      fetchUserGames();
      fetchGameSettings();
    } else {
      setMounted(false);
      setTimeout(() => setVisible(false), 300);
    }
  }, [isOpen]);

  const fetchUserGames = async () => {
    try {
      const response = await gamesApi.getUserGames(tokens.access.token);
      if (response) {
        setUserGames(
          response.map((game) => ({
            ...game,
            dropdownGameName:
              user.roleId === 9
                ? game.name.toUpperCase()
                : game.name +
                  ` (₱${toCurrency(game.winRate)}|${toCurrency(
                    game.commissionRate
                  )}%)`,
          }))
        );
      }
    } catch (error) {
      console.error('Error fetching user games:', error);
      toast.error('Failed to load games');
    }
  };

  const fetchGameSettings = async () => {
    try {
      const response = await gamesApi.getAllGames(tokens.access.token);
      if (response) {
        setGameSettingsOptions(
          Array.isArray(response.data) ? response.data : []
        );
      }
    } catch (error) {
      console.error('Error fetching game settings:', error);
      toast.error('Failed to load game settings');
      setGameSettingsOptions([]);
    }
  };

  // Helper function to format currency
  const toCurrency = (value) => {
    return parseFloat(value).toFixed(2);
  };

  if (!visible) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      let response;
      if (user.roleId !== 1) {
        response = await gamesApi.createGame(tokens.access.token, {
          name,
          commissionRate: parseInt(commissionRate) || 0,
          winRate: parseInt(winRate) || 0,
          doubleWin: parseInt(doubleWinRate) || 0,
          tripleWin: parseInt(tripleWinRate) || 0,
          gameSettingsId,
          description: `${name} (${commissionRate}%)`,
          userId: user.id,
          gameId: gameId,
        });
      } else {
        response = await gamesApi.createGame(tokens.access.token, {
          name,
          commissionRate: parseInt(commissionRate) || 0,
          winRate: parseInt(winRate) || 0,
          doubleWin: parseInt(doubleWinRate) || 0,
          tripleWin: parseInt(tripleWinRate) || 0,
          gameSettingsId,
          description: `${name} (${commissionRate}%)`,
          userId: user.id,
        });
      }

      if (response.status === 201) {
        Swal.fire({
          title: 'Success',
          text: 'Game created successfully',
          icon: 'success',
        });
        onClose();
        // Refresh games list
        if (refreshGames) {
          refreshGames();
        }
        // Reset form
        setName('');
        setCommissionRate('');
        setWinRate('');
        setDoubleWinRate('');
        setTripleWinRate('');
        setSelectedGame('');
        setGameSettingsId('');
        setGameId('');
      } else {
        Swal.fire({
          title: 'Error',
          text: 'Failed to create game',
          icon: 'error',
        });
      }
    } catch (error) {
      Swal.fire({
        title: 'Error',
        text: error.response.data.message,
        icon: 'error',
      });
      console.error('Error creating game:', error);
    }
  };

  return (
    <div
      className='fixed inset-0 bg-black/50 flex items-center justify-center px-4 z-[60] font-poppins'
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className={`w-full max-w-sm bg-white rounded-3xl transform transition-transform duration-300 ${
          mounted ? 'translate-y-0' : 'translate-y-full'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className='py-5 bg-[#0E64D1] rounded-t-3xl text-white'>
          <h2 className='font-bold text-center'>Add Game</h2>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className='px-4'>
          <div className='my-3'>
            <Label htmlFor='name'>Name</Label>
            <TextInput
              id='name'
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder='Create game name'
              className='w-full rounded-md'
            />
          </div>

          {user.roleId !== 1 && (
            <div className='my-3 relative'>
              <Label htmlFor='game'>Select Game</Label>
              <button
                id='game'
                onClick={(e) => {
                  e.preventDefault();
                  setUserGamesDropdownOpen(!userGamesDropdownOpen);
                }}
                className='w-full text-sm px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none flex justify-between'
              >
                <span className='font-light text-sm text-gray-500'>
                  {selectedGame || 'Please Select Game'}
                </span>
                {userGamesDropdownOpen ? (
                  <ChevronUp className='h-4 w-4' />
                ) : (
                  <ChevronDown className='h-4 w-4' />
                )}
              </button>

              {userGamesDropdownOpen && (
                <ul className='absolute w-full mt-1 bg-white border rounded-md shadow-md z-10'>
                  {userGames.map((game, index) => (
                    <li
                      key={game.id}
                      onClick={() => {
                        setSelectedGame(
                          `${game.name} (₱${toCurrency(
                            game.winRate
                          )}|${toCurrency(game.commissionRate)}%)`
                        );
                        setGameSettingsId(game.game_settings_id);
                        setDropdownOpen(false);
                        setGameId(game.id);
                        setUserGamesDropdownOpen(false);
                      }}
                      className={`p-2.5 text-xs text-black hover:bg-[#E1E7EF] cursor-pointer ${
                        index !== 0 ? 'border-t border-[#E1E7EF]' : ''
                      }`}
                    >
                      {game.dropdownGameName}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {user.roleId === 1 && (
            <div className='my-3 relative'>
              <Label>Game Settings</Label>
              <button
                id='gameSettings'
                onClick={(e) => {
                  e.preventDefault();
                  setGameSettingsDropdownOpen(!gameSettingsDropdownOpen);
                }}
                className='w-full text-sm px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none flex justify-between'
              >
                <span className='font-light text-sm text-gray-500'>
                  {selectedGame || 'Game Type'}
                </span>
                {gameSettingsDropdownOpen ? (
                  <ChevronUp className='h-4 w-4' />
                ) : (
                  <ChevronDown className='h-4 w-4' />
                )}
              </button>

              {gameSettingsDropdownOpen && (
                <ul className='absolute w-full mt-1 bg-white border rounded-md shadow-md z-10 max-h-60 overflow-y-auto'>
                  {Array.isArray(gameSettingsOptions) &&
                    gameSettingsOptions.map((gameSetting, index) => (
                      <li
                        key={gameSetting.id}
                        onClick={() => {
                          setSelectedGame(gameSetting.name);
                          setGameSettingsId(gameSetting.id);
                          setGameSettingsDropdownOpen(false);
                        }}
                        className={`p-2.5 text-xs text-black hover:bg-[#E1E7EF] cursor-pointer ${
                          index !== 0 ? 'border-t border-[#E1E7EF]' : ''
                        }`}
                      >
                        {gameSetting.name}
                        {gameSetting.otherSettings &&
                          gameSetting.otherSettings.drawTime && (
                            <div className='text-xs text-gray-500 mt-1'>
                              Draw times:{' '}
                              {gameSetting.otherSettings.drawTime.join(', ')}
                            </div>
                          )}
                      </li>
                    ))}
                </ul>
              )}
            </div>
          )}

          <div className='my-3'>
            <Label>Commission Rate</Label>
            <TextInput
              value={commissionRate}
              onChange={(e) => setCommissionRate(e.target.value)}
              placeholder='0'
              className='w-full rounded-md'
            />
          </div>

          <div className='my-3'>
            <Label>
              {selectedGame === '4D' || selectedGame === '6D'
                ? '1st Prize'
                : 'Win Rate'}
            </Label>
            <TextInput
              value={winRate}
              onChange={(e) => setWinRate(e.target.value)}
              placeholder='0'
              className='w-full rounded-md'
            />
          </div>

          <div className='my-3'>
            <Label>
              {selectedGame === '4D' || selectedGame === '6D'
                ? '2nd Prize'
                : 'Double Win Rate'}
            </Label>
            <TextInput
              value={doubleWinRate}
              onChange={(e) => setDoubleWinRate(e.target.value)}
              placeholder='0'
              className='w-full rounded-md'
            />
          </div>

          <div className='my-3'>
            <Label>
              {selectedGame === '4D' || selectedGame === '6D'
                ? '3rd Prize'
                : 'Triple Win Rate'}
            </Label>
            <TextInput
              value={tripleWinRate}
              onChange={(e) => setTripleWinRate(e.target.value)}
              placeholder='0'
              className='w-full rounded-md'
            />
          </div>

          <div className='flex gap-3 mb-4'>
            <button
              type='button'
              onClick={onClose}
              className='flex-1 p-2 border border-blue-600 text-blue-600 text-sm rounded-lg hover:bg-gray-50 transition-colors'
            >
              Cancel
            </button>
            <button
              type='submit'
              className='flex-1 p-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-600 transition-colors'
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
